import React, { FC, useState } from 'react';
import Popup from 'ati-ui-react/components/Popup';
import { DateRange } from 'react-date-range';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import format from 'date-fns/format';
import ru from 'date-fns/locale/ru';
import Button from 'ati-ui-react/components/Button';
import theme from 'theme';
import cn from 'classnames';
import flatten from 'lodash/flatten';
import isEmpty from 'lodash/isEmpty';
import CloseIcon from 'ati-ui-react/components/icons/Close';
import { observer } from 'mobx-react-lite';
import { useStores } from 'hooks/useStores';
import { IRangeDatePiker } from 'interfaces/IRange';
import {
  DatePickerStyles,
  DatePickerFooter,
  SelectedDateItemStyles,
  SelectedDatesListContainer,
} from './DatePickerStyles';

interface IDateItem {
  [key: string]: IRangeDatePiker;
}

const colorRange = theme.colors.primary;

const initialRange: IRangeDatePiker = {
  startDate: new Date(),
  endDate: new Date(),
  key: 'selection',
  color: colorRange,
};

const getDisabledDates = (ranges: IRangeDatePiker[]) =>
  flatten(
    ranges.map((range) => {
      return eachDayOfInterval({ start: range.startDate, end: range.endDate });
    }),
  );

const stringifyRange = (date: IRangeDatePiker) => {
  const dateFrom = format(date.startDate, 'dd.MM.yyyy');
  const dateTo = format(date.endDate, 'dd.MM.yyyy');
  if (dateFrom === dateTo) return String(dateFrom);
  return `${dateFrom} - ${dateTo}`;
};

interface IDatePicker {
  title: string;
  onClose: () => void;
  onSave?: (ranges: IRangeDatePiker[]) => void;
  readOnly?: boolean;
  ranges?: IRangeDatePiker[];
}

const DatePicker: FC<IDatePicker> = observer(
  ({ ranges = [], title, onClose, onSave, readOnly }) => {
    const {
      appStore: { isDesktop, isLaptop },
    } = useStores();

    const [key, setKey] = useState(0);
    const [range, setRange] = useState(initialRange);
    const [isPointerStartDate, setPointerStartDate] = useState(true);
    const [selectedRanges, setSelectedRanges] = useState(ranges);

    const countShowedMonths = isDesktop || isLaptop ? 2 : 1;
    const isInitialState = ranges.length === selectedRanges.length && isPointerStartDate;

    const resetSelectedDates = () => {
      setSelectedRanges([]);
      setRange(initialRange);
      setPointerStartDate(true);
      setKey((k) => k + 1);
    };

    const handleChange = (item: IDateItem) => {
      if (readOnly) return;
      if (!isPointerStartDate) {
        // @ts-ignore
        setSelectedRanges((prevRanges) => [...prevRanges, { ...item.selection, disabled: true }]);
        const { endDate } = item.selection;
        setRange({ ...initialRange, startDate: endDate, endDate });
      } else {
        setRange(item.selection);
      }
      setPointerStartDate((value) => !value);
    };

    const removeDate = (index: number) => {
      if (selectedRanges.length === 1) {
        resetSelectedDates();
        return;
      }
      if (selectedRanges.length === index + 1) {
        const { endDate } = selectedRanges[index - 1];
        setRange({ ...initialRange, startDate: endDate, endDate });
      }
      setSelectedRanges((prevRanges) => prevRanges.filter((_, i) => i !== index));
    };

    const handleOnSave = () => {
      if (typeof onSave === 'function') {
        onSave(selectedRanges);
      }
    };

    const SelectedDateItem = ({ rangeDate, index }: { rangeDate: string; index: number }) => (
      <SelectedDateItemStyles>
        <span className="text">{rangeDate}</span>
        {!readOnly && (
          <div onClick={() => removeDate(index)}>
            <CloseIcon className="icon" width={12} height={12} />
          </div>
        )}
      </SelectedDateItemStyles>
    );

    const SelectedDatesList = () => (
      <SelectedDatesListContainer className={cn({ 'view-only': readOnly })}>
        {readOnly && !isEmpty(ranges) && <span className="list-title">Недоступно:</span>}
        {selectedRanges.map((date, index) => {
          const rangeDate = stringifyRange(date);
          return <SelectedDateItem key={rangeDate} index={index} rangeDate={rangeDate} />;
        })}
      </SelectedDatesListContainer>
    );

    const footerReadOnly = (
      <DatePickerFooter>
        <Button className="save-btn" onClick={onClose}>
          Закрыть
        </Button>
      </DatePickerFooter>
    );

    const footer = (
      <DatePickerFooter>
        <div className="save-and-reset-wrapper">
          <Button className="save-btn" onClick={handleOnSave}>
            Сохранить
          </Button>
          <Button className="reset-btn" simple simpleDashed onClick={onClose}>
            Отмена
          </Button>
        </div>
        {!isEmpty(selectedRanges) && (
          <Button className="clear-btn" simple simpleDashed simpleGray onClick={resetSelectedDates}>
            Очистить календарь
          </Button>
        )}
      </DatePickerFooter>
    );

    return (
      <DatePickerStyles className={cn({ 'init-state': isInitialState, 'read-only': readOnly })}>
        <Popup
          title={title}
          contentClassName="popup-content"
          whiteActionArea
          onClose={onClose}
          footer={readOnly ? footerReadOnly : footer}
          closeOnOverlayClick={readOnly}
        >
          <div className="date-picker-container" key={key}>
            <DateRange
              showDateDisplay={false}
              months={countShowedMonths}
              direction="horizontal"
              locale={ru}
              // @ts-ignore
              onChange={handleChange}
              disabledDates={getDisabledDates(selectedRanges)}
              ranges={[range]}
              fixedHeight
              weekdayDisplayFormat="EEEEEE"
            />
          </div>
          <SelectedDatesList />
        </Popup>
      </DatePickerStyles>
    );
  },
);

export default DatePicker;
